/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * HEROs UI primitives — ported from the prototype `ui.jsx`.
 *
 * Icons, logo/mark, Sidebar, chips/badges, Avatar, Modal, number/date helpers,
 * relTime, the ToastProvider/useToast pair, and TopBar. Visual design is kept
 * EXACTLY: same class names, same accent (#9E2B25), same Linear-style chips.
 *
 * The prototype reached into `window.HERO_DATA`; the few primitives that need
 * data (`CategoryChip`, `UserBy`) now read it from `useData()`.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore,
} from 'react';
import type { ReactNode, CSSProperties } from 'react';
import React from 'react';
import { useData } from '../../context/AppDataContext';

/* ============ Icons (tiny inline SVG, currentColor) ============ */
export const Ico = {
  search: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  dash: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" /></svg>,
  cal: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>,
  fold: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>,
  doc: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /></svg>,
  receipt: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h14v18l-3-2-3 2-3-2-3 2-2-1.5z" /><path d="M9 9h6M9 13h6" /></svg>,
  users: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5" /><path d="M2 20c1-3.5 4-5 7-5s6 1.5 7 5" /><circle cx="17" cy="6" r="2.5" /><path d="M22 18c-.5-2-2-3.5-4-4" /></svg>,
  stats: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>,
  cog: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3.9a7 7 0 0 0-2.1-1.2L14 3h-4l-.5 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.5 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.3-.9a7 7 0 0 0 2.1 1.2L10 21h4l.5-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.5-2-1.5c.1-.4.1-.8.1-1.2z" /></svg>,
  plus: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  filter: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 9v5l-4 2v-7z" /></svg>,
  chev: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>,
  chevDown: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>,
  chevLeft: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 6-6 6 6 6" /></svg>,
  close: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m6 6 12 12M18 6 6 18" /></svg>,
  dot3: () => <svg className="ico" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>,
  eye: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" /><circle cx="12" cy="12" r="3" /></svg>,
  edit: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m4 20 4-1 11-11-3-3L5 16zM14 6l3 3" /></svg>,
  trash: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></svg>,
  check: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6" /></svg>,
  dl: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v12m0 0-4-4m4 4 4-4M4 20h16" /></svg>,
  logout: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 17l5-5-5-5M21 12H9M9 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" /></svg>,
  sun: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" /></svg>,
  moon: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>,
  bell: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 5 2 7 2 7H4s2-2 2-7" /><path d="M10 19a2 2 0 0 0 4 0" /></svg>,
  flag: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4v17M4 4h14l-3 5 3 5H4" /></svg>,
  beaker: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6M10 3v6L4 19a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-6-10V3" /></svg>,
  arrowR: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14m-5-5 5 5-5 5" /></svg>,
  print: () => <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V3h12v6M6 18H4v-7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v7h-2M6 14h12v7H6z" /></svg>,
};

/* ============ HEROs logo (compact mark + wordmark) ============ */
/** Square brand mark — the HEROs elephant. Served from /public (content rarely changes). */
export function HerosMark({ size = 32 }: { size?: number }) {
  return (
    <img
      src="/hero-mark.png"
      width={size}
      height={size}
      alt="HEROs"
      style={{ display: 'block', borderRadius: Math.round(size * 0.2), objectFit: 'contain', flexShrink: 0 }}
    />
  );
}

/** Full HEROs wordmark (transparent PNG, navy/red on light). Use on light surfaces only. */
export function HerosWordmark({ height = 32 }: { height?: number }) {
  return <img src="/hero-wordmark.png" height={height} alt="HEROs" style={{ display: 'block', width: 'auto', objectFit: 'contain' }} />;
}

export function HerosLogoFull() {
  return <HerosWordmark height={40} />;
}

/* ============ Sidebar ============ */
export interface SidebarNavItem {
  id: string;
  label: string;
  ico: () => JSX.Element;
  badge?: number;
}
export interface SidebarGroup { group: string; items: SidebarNavItem[]; }

/** Where the QuickFind palette navigates when a result is chosen. App resolves each kind. */
export type QuickNavTarget =
  | { kind: 'route'; route: string }
  | { kind: 'project'; id: string }
  | { kind: 'task'; projectId: string }
  | { kind: 'person'; id: string };

export interface SidebarProps {
  route: string;
  setRoute: (r: string) => void;
  currentUser: any;
  /** Pre-built, role-filtered nav groups (App owns the gating). */
  groups: SidebarGroup[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** Sign the current user out (clears the session). */
  onSignOut?: () => void;
  /** Navigate from the QuickFind (⌘K) palette. When omitted the palette hides. */
  onQuickNav?: (t: QuickNavTarget) => void;
}

/** Light/dark theme toggle. Persists to localStorage('heros-theme') and sets <html data-theme>. */
export function useTheme(): ['light' | 'dark', () => void] {
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'),
  );
  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        if (next === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
        else document.documentElement.removeAttribute('data-theme');
        localStorage.setItem('heros-theme', next);
      } catch { /* noop */ }
      return next;
    });
  }, []);
  return [theme, toggle];
}

/* ---- User-adjustable UI/text scale (zooms the whole app, like browser zoom) ---- */
export const UI_SCALE_MIN = 90;
export const UI_SCALE_MAX = 170;
export const UI_SCALE_STEP = 10;

/** Apply a UI scale (%): zoom the #root mount, and publish the inverse so viewport
 *  units (`100vw/100vh` on #root, modals, etc.) cancel the zoom and stay on-screen. */
export function applyUiScale(pct: number): void {
  const scale = Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, pct)) / 100;
  // Clear any stale <html> zoom from the earlier approach (avoids double-zoom on upgrade).
  (document.documentElement.style as unknown as Record<string, string>).zoom = '';
  const mount = document.getElementById('root');
  if (mount) (mount.style as unknown as Record<string, string>).zoom = String(scale);
  document.documentElement.style.setProperty('--dc-inv-zoom', String(1 / scale));
}

/** Persisted text/UI scale in percent. Setter clamps, persists, and applies live. */
export function useUiScale(): [number, (next: number) => void] {
  const [pct, setPct] = useState<number>(() => {
    try {
      const v = parseInt(localStorage.getItem('heros-ui-scale') || '', 10);
      if (!Number.isNaN(v)) return Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, v));
    } catch { /* noop */ }
    return 100;
  });
  useEffect(() => {
    applyUiScale(pct);
    try { localStorage.setItem('heros-ui-scale', String(pct)); } catch { /* noop */ }
  }, [pct]);
  const set = useCallback((next: number) => {
    setPct(Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, Math.round(next))));
  }, []);
  return [pct, set];
}

/** Min/max draggable sidebar width (px). */
const SIDEBAR_MIN = 180;
const SIDEBAR_MAX = 460;

/* ---- QuickFind (⌘K) command palette in the sidebar search slot ---- */
interface QuickResult {
  key: string;
  kind: QuickNavTarget['kind'];
  label: string;
  sub?: string;
  target: QuickNavTarget;
}

/** Static routes mirror the Shell sidebar ids (App.tsx). Gated admin routes are left out. */
const QUICK_ROUTES: { id: string; label: string }[] = [
  { id: 'dashboard', label: 'Monthly Team Dashboard' },
  { id: 'projects', label: 'Projects' },
  { id: 'portfolio', label: 'Portfolio Overview' },
  { id: 'workload', label: 'Team Workload' },
  { id: 'mytasks', label: 'My Tasks' },
  { id: 'earnings', label: 'My Earnings' },
  { id: 'settings', label: 'My Profile' },
];

const QUICK_KIND_META: Record<QuickNavTarget['kind'], { badge: string; tone: string }> = {
  route: { badge: 'Go', tone: 'slate' },
  project: { badge: 'Project', tone: 'blue' },
  task: { badge: 'Task', tone: 'amber' },
  person: { badge: 'Person', tone: 'green' },
};

/** Sidebar search-slot palette: fuzzy jump to routes / projects / tasks / people.
 *  Collapsed sidebar → renders nothing and the ⌘K shortcut no-ops (no input to focus). */
function QuickFind({ collapsed, onQuickNav }: { collapsed: boolean; onQuickNav?: (t: QuickNavTarget) => void }) {
  const D = useData();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  // ⌘K / Ctrl+K focuses the input (registered once; no-op when collapsed → input unmounted).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const projById = useMemo(() => {
    const m: Record<string, any> = {};
    for (const p of D.projects) m[p.id] = p;
    return m;
  }, [D.projects]);

  const results = useMemo<QuickResult[]>(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    const out: QuickResult[] = [];
    for (const r of QUICK_ROUTES) {
      if (r.label.toLowerCase().includes(term) || r.id.includes(term)) {
        out.push({ key: 'route:' + r.id, kind: 'route', label: r.label, target: { kind: 'route', route: r.id } });
      }
    }
    for (const p of D.projects) {
      if (`${p.code} ${p.shortTitle} ${p.title}`.toLowerCase().includes(term)) {
        out.push({ key: 'project:' + p.id, kind: 'project', label: `${p.code} · ${p.shortTitle}`, sub: p.title, target: { kind: 'project', id: p.id } });
      }
    }
    for (const u of D.team) {
      if (`${u.name} ${u.role}`.toLowerCase().includes(term)) {
        out.push({ key: 'person:' + u.id, kind: 'person', label: u.name, sub: u.role, target: { kind: 'person', id: u.id } });
      }
    }
    for (const t of D.timeline) {
      if ((t.name || '').toLowerCase().includes(term)) {
        out.push({ key: 'task:' + t.id, kind: 'task', label: t.name, sub: projById[t.project]?.code || '', target: { kind: 'task', projectId: t.project } });
      }
    }
    return out.slice(0, 8);
  }, [q, D.projects, D.team, D.timeline, projById]);

  useEffect(() => { setActive(0); }, [q]);

  const choose = (r?: QuickResult) => {
    const pick = r ?? results[active];
    if (!pick) return;
    onQuickNav?.(pick.target);
    setQ(''); setOpen(false);
  };

  if (collapsed) return null;

  return (
    <div className="sidebar-search" style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        placeholder="Quick find…  ⌘K"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(Math.max(results.length - 1, 0), i + 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
          else if (e.key === 'Enter') { e.preventDefault(); choose(); }
          else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
        }}
      />
      {open && results.length > 0 && (
        <div role="listbox" style={{
          position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', zIndex: 55,
          background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 8,
          boxShadow: 'var(--shadow-md)', padding: 4, display: 'flex', flexDirection: 'column', maxHeight: 320, overflowY: 'auto',
        }}>
          {results.map((r, i) => (
            <button
              key={r.key}
              role="option"
              aria-selected={i === active}
              // mousedown (not click) so we act before the input's blur closes the panel.
              onMouseDown={(e) => { e.preventDefault(); choose(r); }}
              onMouseEnter={() => setActive(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                padding: '6px 8px', border: 0, borderRadius: 6, cursor: 'pointer', font: 'inherit',
                background: i === active ? 'var(--surface-alt)' : 'transparent',
              }}
            >
              <span className="chip" data-tone={QUICK_KIND_META[r.kind].tone} style={{ flexShrink: 0, fontSize: 9.5 }}>{QUICK_KIND_META[r.kind].badge}</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
                {r.sub ? <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.sub}</span> : null}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ route, setRoute, currentUser, groups, collapsed, onToggleCollapse, onSignOut, onQuickNav }: SidebarProps) {
  const [theme, toggleTheme] = useTheme();
  const [uiScale, setUiScale] = useUiScale();
  const asideRef = useRef<HTMLElement | null>(null);

  // Restore the user's saved sidebar width on mount.
  useEffect(() => {
    try {
      const saved = parseInt(localStorage.getItem('heros-sidebar-w') || '', 10);
      if (!Number.isNaN(saved)) {
        const w = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, saved));
        document.documentElement.style.setProperty('--sidebar-w', w + 'px');
      }
    } catch { /* noop */ }
  }, []);

  // Drag the right edge to resize; writes --sidebar-w live and persists on release.
  const onResizeStart = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = asideRef.current?.getBoundingClientRect().width ?? 240;
    document.body.classList.add('resizing-sidebar');
    let lastW = startW;
    const move = (ev: PointerEvent) => {
      lastW = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(startW + (ev.clientX - startX))));
      document.documentElement.style.setProperty('--sidebar-w', lastW + 'px');
    };
    const up = () => {
      document.body.classList.remove('resizing-sidebar');
      try { localStorage.setItem('heros-sidebar-w', String(lastW)); } catch { /* noop */ }
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <aside ref={asideRef} className={'sidebar' + (collapsed ? ' collapsed' : '')}>
      <div className="sidebar-brand">
        <HerosMark size={36} />
        {!collapsed && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1>HEROs</h1>
            <small>Research Operations</small>
          </div>
        )}
        <button className="btn ghost icon sm" title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} onClick={onToggleCollapse} style={{ marginLeft: collapsed ? 0 : 'auto' }}>
          {collapsed ? <Ico.chev /> : <Ico.chevLeft />}
        </button>
      </div>
      <QuickFind collapsed={collapsed} onQuickNav={onQuickNav} />
      {groups.map((g) => (
        <div className="nav-section" key={g.group}>
          {!collapsed && <div className="nav-section-title">{g.group}</div>}
          {g.items.map((it) => {
            const Icon = it.ico;
            return (
              <button
                key={it.id}
                className={'nav-item ' + (route === it.id ? 'active' : '')}
                onClick={() => setRoute(it.id)}
                title={collapsed ? it.label : undefined}
                style={collapsed ? { justifyContent: 'center', padding: '8px 0' } : {}}
              >
                <Icon />
                {!collapsed && <span>{it.label}</span>}
                {!collapsed && it.badge ? <span className="badge-num tabular">{it.badge}</span> : null}
              </button>
            );
          })}
        </div>
      ))}
      <div className="sidebar-user">
        <Avatar user={currentUser} />
        {!collapsed && (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{currentUser?.name}</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentUser?.role}</div>
            </div>
            <button className="btn ghost icon sm" title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} onClick={toggleTheme}>{theme === 'dark' ? <Ico.sun /> : <Ico.moon />}</button>
            <button className="btn ghost icon sm" title="System" onClick={() => setRoute('system')}><Ico.cog /></button>
            <button className="btn ghost icon sm" title="Sign out" onClick={() => onSignOut?.()}><Ico.logout /></button>
          </>
        )}
        {collapsed && (
          <>
            <button className="btn ghost icon sm" title={theme === 'dark' ? 'Light mode' : 'Dark mode'} onClick={toggleTheme}>{theme === 'dark' ? <Ico.sun /> : <Ico.moon />}</button>
            <button className="btn ghost icon sm" title="Sign out" onClick={() => onSignOut?.()}><Ico.logout /></button>
          </>
        )}
      </div>
      {!collapsed && (
        <div className="sidebar-scale" title="Adjust text size for this device">
          <span className="lbl">Text size</span>
          <div className="ctl">
            <button type="button" aria-label="Smaller text" title="Smaller" disabled={uiScale <= UI_SCALE_MIN} onClick={() => setUiScale(uiScale - UI_SCALE_STEP)}>A−</button>
            <span className="val tabular" aria-live="polite">{uiScale}%</span>
            <button type="button" aria-label="Larger text" title="Larger" disabled={uiScale >= UI_SCALE_MAX} onClick={() => setUiScale(uiScale + UI_SCALE_STEP)}>A+</button>
            {uiScale !== 100 && <button type="button" className="reset" aria-label="Reset text size" title="Reset to 100%" onClick={() => setUiScale(100)}>↺</button>}
          </div>
        </div>
      )}
      {collapsed && (
        <button className="btn ghost icon sm" title={`Text size ${uiScale}% — click to enlarge`} style={{ margin: '0 auto 6px' }} aria-label="Increase text size" onClick={() => setUiScale(uiScale >= UI_SCALE_MAX ? 100 : uiScale + UI_SCALE_STEP)}>
          <span style={{ fontWeight: 800, fontSize: 13 }}>A</span>
        </button>
      )}
      {!collapsed && (
        <div className="sidebar-foot">
          <div><strong>BioCE</strong> · Faculty of Medicine, CMU</div>
          <div>v0.4.0 · MVP preview</div>
        </div>
      )}
      {!collapsed && (
        <div
          className="sidebar-resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          title="Drag to resize · double-click to reset"
          onPointerDown={onResizeStart}
          onDoubleClick={() => {
            document.documentElement.style.removeProperty('--sidebar-w');
            try { localStorage.removeItem('heros-sidebar-w'); } catch { /* noop */ }
          }}
        />
      )}
    </aside>
  );
}

/* ============ Chip / status helpers ============ */
export function CategoryChip({ category }: { category: string; size?: string }) {
  const D = useData();
  const tone = (D.categories[category] || { tone: 'slate' }).tone;
  return <span className="chip" data-tone={tone}><span className="dot" />{category}</span>;
}

export const STATUS_META: Record<string, { label: string; tone: string }> = {
  planned: { label: 'Planned', tone: 'slate' },
  'in-progress': { label: 'In progress', tone: 'blue' },
  waiting: { label: 'Waiting', tone: 'amber' },
  completed: { label: 'Completed', tone: 'green' },
  delayed: { label: 'Delayed', tone: 'red' },
  cancelled: { label: 'Cancelled', tone: 'slate' },
};

export function StatusBadge({ status }: { status: string }) {
  const m = STATUS_META[status] || STATUS_META.planned;
  return <span className="chip" data-tone={m.tone}><span className="dot" />{m.label}</span>;
}

/* ============ Avatar photo store ============ *
 * Real, user-uploaded profile photos persisted in localStorage as small
 * downscaled JPEG data-URLs (see Settings → Change avatar). Kept in ONE JSON
 * map under `heros:avatars` and exposed through an external store so every
 * <Avatar/> across the app updates instantly when a photo changes. */
const AVATAR_KEY = 'heros:avatars';
function readAvatars(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(AVATAR_KEY) || '{}'); } catch { return {}; }
}
let avatarCache: Record<string, string> = readAvatars();
const avatarSubs = new Set<() => void>();
const avatarStore = {
  subscribe(cb: () => void) { avatarSubs.add(cb); return () => avatarSubs.delete(cb); },
  snapshot() { return avatarCache; },
  set(id: string, dataUrl: string) {
    avatarCache = { ...avatarCache, [id]: dataUrl };
    try { localStorage.setItem(AVATAR_KEY, JSON.stringify(avatarCache)); } catch { /* quota */ }
    avatarSubs.forEach((f) => f());
  },
  remove(id: string) {
    const next = { ...avatarCache }; delete next[id]; avatarCache = next;
    try { localStorage.setItem(AVATAR_KEY, JSON.stringify(avatarCache)); } catch { /* ignore */ }
    avatarSubs.forEach((f) => f());
  },
};
/** Read one user's uploaded avatar (re-renders when it changes). */
export function useAvatar(id?: string): string | undefined {
  const map = useSyncExternalStore(avatarStore.subscribe, avatarStore.snapshot, avatarStore.snapshot);
  return id ? map[id] : undefined;
}
export const avatars = { set: avatarStore.set, remove: avatarStore.remove, get: (id: string) => avatarCache[id] };

/** Downscale + crop an image File to a square JPEG data-URL (default 128px) so
 *  uploaded avatars stay a few KB in storage. Returns a Promise<dataURL>. */
export function downscaleImage(file: File, size = 128, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const side = Math.min(img.width, img.height);
      const sx = (img.width - side) / 2; const sy = (img.height - side) / 2;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas unsupported'));
      ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });
}

export function Avatar({ user, size = 'md' }: { user?: any; size?: 'sm' | 'md' | 'lg' }) {
  const photo = useAvatar(user?.id);
  if (!user) return <div className={'avatar ' + (size === 'sm' ? 'sm' : '')}>?</div>;
  const cls = 'avatar' + (size === 'sm' ? ' sm' : size === 'lg' ? ' lg' : '');
  const photoSrc = photo || user.avatar;
  if (photoSrc) return <img className={cls} src={photoSrc} alt={user.name} title={user.name} style={{ objectFit: 'cover' }} />;
  const tints = ['#1F2A55', '#9E2B25', '#2A57A8', '#2E6A3C', '#8A5A14', '#6B3E96'];
  const idx = parseInt((user.id || 'u0').replace(/\D/g, ''), 10) % tints.length;
  return <div className={cls} style={{ background: tints[idx] }} title={user.name}>{user.initials || (user.name || '?').slice(0, 2)}</div>;
}

export function UserBy({ id }: { id: string }) {
  const D = useData();
  const u = D.team.find((x) => x.id === id);
  if (!u) return <span className="mono" style={{ color: 'var(--text-3)' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Avatar user={u} size="sm" />
      <span>{u.name}</span>
    </span>
  );
}

/* ============ Modal ============ */
export interface ModalProps {
  title: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}
export function Modal({ title, onClose, children, footer, wide }: ModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" style={wide ? { width: 720 } : {}} onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <h3>{title}</h3>
          <button className="btn ghost icon" onClick={onClose}><Ico.close /></button>
        </div>
        <div className="modal-b">{children}</div>
        {footer && <div className="modal-f">{footer}</div>}
      </div>
    </div>
  );
}

/* ============ Number formatting ============ */
export const fmt = {
  thb: (n?: number) => '฿' + (n || 0).toLocaleString('en-US'),
  num: (n?: number) => (n || 0).toLocaleString('en-US'),
  pct: (n?: number) => (n || 0).toFixed(0) + '%',
};

/* ============ Date helpers ============ */
export const HERO_YEAR = 2026;
export function dayOfYear(iso?: string): number {
  if (!iso) return 0;
  const d = new Date(iso + 'T00:00:00');
  const start = new Date(d.getFullYear(), 0, 0);
  return Math.floor((d.getTime() - start.getTime()) / 86400000);
}
export function daysInYear(y: number): number {
  return ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) ? 366 : 365;
}
export function monthFromIso(iso: string): number { return new Date(iso + 'T00:00:00').getMonth(); }
export function isoFirstOfMonth(m: number): string { return `${HERO_YEAR}-${String(m + 1).padStart(2, '0')}-01`; }
export function isoLastOfMonth(m: number): string {
  const d = new Date(HERO_YEAR, m + 1, 0).getDate();
  return `${HERO_YEAR}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
export function taskDates(t: { start: number; end: number; startDate?: string; endDate?: string }) {
  return {
    startDate: t.startDate || isoFirstOfMonth(t.start),
    endDate: t.endDate || isoLastOfMonth(t.end),
  };
}
export function fmtShortDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

/* ============ Month-key helpers (multi-year month axis) ============
 * A month key = year*12 + month(0-11) — an absolute month number that works
 * across years (the legacy 0-11 `start`/`end` ints alias every year onto
 * 2026, so multi-year UIs must derive their axis from these keys instead). */
export const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function monthKeyFromIso(iso: string): number {
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d.getTime()) ? NaN : d.getFullYear() * 12 + d.getMonth();
}
export function monthKeyLabel(k: number): string { return `${MONTH_ABBR[((k % 12) + 12) % 12]} ${Math.floor(k / 12)}`; }
export function todayMonthKey(): number { const n = new Date(); return n.getFullYear() * 12 + n.getMonth(); }
/** A task's [startKey, endKey] month-key span — ISO dates first, legacy 2026
 *  month-index fallback for rows without dates. */
export function taskMonthKeys(t: { start: number; end: number; startDate?: string; endDate?: string }) {
  const td = taskDates(t);
  let sK = monthKeyFromIso(td.startDate);
  let eK = monthKeyFromIso(td.endDate);
  if (isNaN(sK)) sK = HERO_YEAR * 12 + (t.start || 0);
  if (isNaN(eK)) eK = HERO_YEAR * 12 + (t.end || 0);
  return { sK, eK: Math.max(sK, eK) };
}
export function isoFirstOfMonthKey(k: number): string {
  const y = Math.floor(k / 12), m = k % 12;
  return `${y}-${String(m + 1).padStart(2, '0')}-01`;
}
export function isoLastOfMonthKey(k: number): string {
  const y = Math.floor(k / 12), m = k % 12;
  const d = new Date(y, m + 1, 0).getDate();
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/* ============ Relative time helper ============ */
export function relTime(stamp?: string): string {
  if (!stamp) return '';
  const now = new Date();
  const then = new Date(stamp.replace(' ', 'T'));
  if (isNaN(then.getTime())) return stamp;
  const sec = Math.floor((now.getTime() - then.getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return Math.floor(sec / 60) + 'm ago';
  if (sec < 86400) return Math.floor(sec / 3600) + 'h ago';
  if (sec < 86400 * 2) return 'yesterday';
  if (sec < 86400 * 7) return Math.floor(sec / 86400) + 'd ago';
  if (sec < 86400 * 60) return Math.floor(sec / (86400 * 7)) + 'w ago';
  return then.toISOString().slice(0, 10);
}

/* ============ Toast ============ */
type ToastFn = (msg: string) => void;
const ToastCtx = createContext<ToastFn | null>(null);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<{ id: number; msg: string }[]>([]);
  const push = useCallback<ToastFn>((msg) => {
    const id = Math.random();
    setToasts((t) => [...t, { id, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div style={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 200 }}>
        {toasts.map((t) => (
          <div key={t.id} style={{ background: 'var(--text)', color: '#fff', padding: '8px 12px', borderRadius: 6, fontSize: 12.5, boxShadow: 'var(--shadow-md)' }}>
            {t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = (): ToastFn => {
  const fn = useContext(ToastCtx);
  return fn ?? (() => undefined);
};

/* ============ Top bar ============ */
export interface Crumb { label: string; onClick?: () => void; }
export function TopBar({ crumbs, actions, onNavToggle, bell }: { crumbs: Crumb[]; actions?: ReactNode; onNavToggle?: () => void; bell?: ReactNode }) {
  return (
    <header className="topbar">
      {onNavToggle && (
        <button className="nav-toggle" onClick={onNavToggle} aria-label="Open navigation" title="Menu">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
        </button>
      )}
      <div className="crumbs">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="sep"><Ico.chev /></span>}
            <span className={i === crumbs.length - 1 ? 'current' : ''} style={{ cursor: c.onClick ? 'pointer' : 'default' } as CSSProperties} onClick={c.onClick}>
              {c.label}
            </span>
          </React.Fragment>
        ))}
      </div>
      <div className="spacer" />
      <div className="toolbar">
        {actions}
        {bell ?? <button className="btn ghost icon" title="Notifications"><Ico.bell /></button>}
      </div>
    </header>
  );
}

/* ============ Row action menu (reusable ⋮ dropdown) ============ */
export interface RowMenuItem {
  label: string;
  onClick: () => void;
  /** Render in the accent/danger colour (e.g. Delete). */
  danger?: boolean;
  /** Disable the item (greyed, non-clickable). */
  disabled?: boolean;
}

/**
 * A small ⋮ button that opens a dropdown of actions. Closes on outside-click,
 * Escape, or after an item runs. Stops row click-through so it works inside
 * clickable table rows.
 */
export function RowMenu({ items, label = 'Actions' }: { items: RowMenuItem[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }} onClick={(e) => e.stopPropagation()}>
      <button className="btn ghost icon sm" title={label} aria-haspopup="menu" aria-expanded={open} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}><Ico.dot3 /></button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 50,
          minWidth: 168, background: 'var(--surface)', border: '1px solid var(--border-strong)',
          borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 4, display: 'flex', flexDirection: 'column',
        }}>
          {items.map((it, i) => (
            <button
              key={i}
              role="menuitem"
              disabled={it.disabled}
              onClick={(e) => { e.stopPropagation(); if (it.disabled) return; setOpen(false); it.onClick(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                padding: '7px 10px', border: 0, background: 'transparent', borderRadius: 6,
                font: 'inherit', fontSize: 12.5, cursor: it.disabled ? 'not-allowed' : 'pointer',
                color: it.disabled ? 'var(--text-3)' : it.danger ? 'var(--accent)' : 'var(--text)',
              }}
              onMouseEnter={(e) => { if (!it.disabled) (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-alt)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Shared mouse drag-to-reorder for table rows. Only a ⠿ grip is draggable, so a drag
 * never fights with text selection in the row's inputs; gripProps spread onto a plain
 * host <span> (stable element) so a mid-drag re-render can't abort the native drag.
 * `commit(nextArray)` receives the reordered list — wire it to local state OR a persist call.
 * Used by the builder (Labs / Outcomes / Timeline) AND the project-detail timeline.
 */
export function useRowDrag(items: any[], commit: (next: any[]) => void) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const move = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    const arr = [...items]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); commit(arr);
  };
  const rowProps = (idx: number) => ({
    onDragOver: (e: any) => { if (dragIdx !== null) { e.preventDefault(); if (overIdx !== idx) setOverIdx(idx); } },
    onDrop: (e: any) => { e.preventDefault(); if (dragIdx !== null) move(dragIdx, idx); setDragIdx(null); setOverIdx(null); },
    style: {
      opacity: dragIdx === idx ? 0.4 : 1,
      boxShadow: (overIdx === idx && dragIdx !== null && dragIdx !== idx) ? (dragIdx < idx ? 'inset 0 -2px 0 var(--accent)' : 'inset 0 2px 0 var(--accent)') : undefined,
      background: (overIdx === idx && dragIdx !== null && dragIdx !== idx) ? 'var(--accent-soft)' : undefined,
    } as CSSProperties,
  });
  const gripProps = (idx: number) => ({
    draggable: true,
    onDragStart: (e: any) => { setDragIdx(idx); setOverIdx(idx); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* some browsers require setData */ } },
    onDragEnd: () => { setDragIdx(null); setOverIdx(null); },
    title: 'Drag to reorder this row',
    style: { cursor: 'grab', padding: '0 4px', color: 'var(--text-3)', fontSize: 13, lineHeight: 1, userSelect: 'none' } as CSSProperties,
  });
  return { move, rowProps, gripProps };
}
